import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { dateTime, initials, plural, WORDS } from '../../lib/format';
import { Icon, type IconName } from '../../components/Icon';
import { EmptyState, ErrorState, Modal, Pager } from '../../components/ui';
import { MailCompose } from '../../components/MailCompose';

interface MailAccount {
  id: string;
  label: string;
  address: string;
  status: string;
  unread: number;
  isActive: boolean;
}

interface MessageRow {
  id: string;
  accountId: string;
  threadId: string | null;
  direction: 'INBOUND' | 'OUTBOUND';
  fromAddress: string;
  fromName: string | null;
  toAddresses: string[];
  subject: string;
  snippet: string;
  sentAt: string;
  isRead: boolean;
  isStarred: boolean;
  hasAttachments: boolean;
  accountRefId: string | null;
  contactId: string | null;
  leadId: string | null;
}

interface MessagePage {
  items: MessageRow[];
  total: number;
  unread: number;
  page: number;
  pageCount: number;
}

interface MessageDetail extends MessageRow {
  ccAddresses: string[];
  bodyText: string | null;
  bodyHtml: string | null;
  attachments: Array<{ filename: string; size: number; contentType: string }>;
  mailAccount: { id: string; label: string; address: string };
}

/** المجلّدات — كسكّة Gmail: الوارد أولًا وهو الافتراضي. */
const FOLDERS: Array<{ key: Folder; label: string; icon: IconName }> = [
  { key: 'inbox', label: 'الوارد', icon: 'inbox' },
  { key: 'starred', label: 'المميّزة', icon: 'star' },
  { key: 'sent', label: 'المرسَلة', icon: 'send' },
  { key: 'unread', label: 'غير المقروءة', icon: 'mail' },
  { key: 'all', label: 'كل البريد', icon: 'layers' },
];

type Folder = 'inbox' | 'starred' | 'sent' | 'unread' | 'all';

/**
 * صندوق البريد — بهيكل Gmail.
 *
 * سكّة مجلّدات وزرّ إنشاء بارز، بحثٌ حبّي في الصدر، صفوفٌ تحمل نجمتها
 * وإجراءاتها، والرسالة تُفتح في مكان القائمة لا في نافذة تحجب الشاشة —
 * فالعادة التي بناها بريد Google في أعين الجميع أرخص أن تُتَّبع من أن
 * يُعاد تعليم الناس سواها.
 *
 * والغرض يبقى ما كان: وضع المراسلة بجوار ملف العميل. الرسالة الواصلة
 * تُربط تلقائيًا بمن أرسلها إن كان معروفًا، فيرى المندوب تاريخ التواصل
 * كاملًا في مكان واحد.
 */
export function Inbox() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [accountId, setAccountId] = useState('');
  const [search, setSearch] = useState('');
  const [folder, setFolder] = useState<Folder>('inbox');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [openId, setOpenId] = useState<string | null>(null);
  const [composing, setComposing] = useState<MessageDetail | 'new' | null>(null);
  const [tasksOpen, setTasksOpen] = useState(false);

  const accounts = useQuery({
    queryKey: ['mail', 'accounts'],
    queryFn: () => api<{ items: MailAccount[] }>('/mail/accounts'),
  });

  const query = {
    accountId: accountId || undefined,
    q: search || undefined,
    page,
    pageSize,
    ...(folder === 'inbox'
      ? { direction: 'INBOUND' }
      : folder === 'sent'
        ? { direction: 'OUTBOUND' }
        : folder === 'starred'
          ? { starredOnly: true }
          : folder === 'unread'
            ? { unreadOnly: true }
            : {}),
  };

  const messages = useQuery({
    queryKey: ['mail', 'messages', query],
    queryFn: () => api<MessagePage>(`/mail/messages${qs(query)}`),
    enabled: (accounts.data?.items.length ?? 0) > 0,
  });

  const sync = useMutation({
    mutationFn: () => api<{ created: number }>('/mail/sync', { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['mail'] }),
  });

  /* نجمة الصف وعلامة القراءة — من القائمة مباشرةً دون فتح. */
  const setFlags = useMutation({
    mutationFn: ({ id, flags }: { id: string; flags: { isRead?: boolean; isStarred?: boolean } }) =>
      api(`/mail/messages/${id}/flags`, { method: 'PATCH', body: flags }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['mail'] }),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['mail'] });

  /*
   * مزامنةٌ تلقائية كل دقيقتين — صامتة.
   *
   * تجري في الخلفية دون تنبيهاتٍ (لا «وصلت رسائل» ولا رسالة خطأ): إزعاجُ
   * تنبيهٍ كلَّ دقيقتين أسوأ من تأخّر رسالة، فالإخفاق يُبتلع والوصول يُبطل
   * قوائمَ البريد بهدوء. أمّا زرّ المزامنة اليدويّ فيبقى يُظهر نتيجته.
   */
  const hasAccounts = (accounts.data?.items.length ?? 0) > 0;
  useEffect(() => {
    if (!hasAccounts) return;
    const t = window.setInterval(() => {
      void api('/mail/sync', { method: 'POST' })
        .then(() => queryClient.invalidateQueries({ queryKey: ['mail'] }))
        .catch(() => undefined);
    }, 120_000);
    return () => window.clearInterval(t);
  }, [queryClient, hasAccounts]);

  const noAccounts = accounts.data?.items.length === 0;
  const totalUnread = (accounts.data?.items ?? []).reduce((n, a) => n + a.unread, 0);

  const pick = (next: Folder) => {
    setFolder(next);
    setPage(1);
    setOpenId(null);
  };

  return (
    <div className="page">
      {accounts.isError && (
        <ErrorState error={accounts.error} onRetry={() => void accounts.refetch()} />
      )}

      {noAccounts && (
        <EmptyState
          title="لا صندوق بريد مربوط"
          text="اربط بريد شركتك من إعدادات البريد ليصل إلى هنا مرتبطًا بملفات العملاء."
          action={
            <Link to="/settings/mail" className="btn btn-primary">
              إعدادات البريد
            </Link>
          }
        />
      )}

      {sync.isSuccess && sync.data.created > 0 && (
        <div className="alert alert-info mb-4">
          وصلت {plural(sync.data.created, WORDS.message)} جديدة.
        </div>
      )}
      {sync.isError && (
        <div className="alert alert-danger mb-4">
          {sync.error instanceof Error ? sync.error.message : 'تعذّرت المزامنة.'}
        </div>
      )}

      {!noAccounts && (
        <div className="mailapp">
          {/* ---------------- السكّة ---------------- */}
          <aside className="mail-rail">
            <button className="mail-compose-btn" onClick={() => setComposing('new')}>
              <Icon name="edit" size={17} />
              إنشاء
            </button>

            <nav className="mail-folders" aria-label="مجلّدات البريد">
              {FOLDERS.map((f) => (
                <button
                  key={f.key}
                  className={`mail-folder${folder === f.key ? ' active' : ''}`}
                  onClick={() => pick(f.key)}
                  aria-current={folder === f.key}
                >
                  <Icon name={f.icon} size={16} />
                  <span className="mail-folder-label">{f.label}</span>
                  {f.key === 'inbox' && totalUnread > 0 && (
                    <span className="mail-folder-count num">{totalUnread}</span>
                  )}
                </button>
              ))}
            </nav>

            {(accounts.data?.items.length ?? 0) > 1 && (
              <div className="mail-accounts">
                <div className="tiny mute mb-2">الصناديق</div>
                <button
                  className={`mail-folder${accountId === '' ? ' active' : ''}`}
                  onClick={() => {
                    setAccountId('');
                    setPage(1);
                  }}
                >
                  <span className="mail-folder-label">كل الصناديق</span>
                </button>
                {accounts.data?.items.map((a) => (
                  <button
                    key={a.id}
                    className={`mail-folder${accountId === a.id ? ' active' : ''}`}
                    onClick={() => {
                      setAccountId(a.id);
                      setPage(1);
                    }}
                    title={a.address}
                  >
                    <span className="mail-folder-label">{a.label}</span>
                    {a.unread > 0 && <span className="mail-folder-count num">{a.unread}</span>}
                  </button>
                ))}
              </div>
            )}
          </aside>

          {/* ---------------- اللوحة ---------------- */}
          <section className="mail-main">
            {openId ? (
              <MessageView
                id={openId}
                onBack={() => setOpenId(null)}
                onChanged={refresh}
                onReply={(m) => {
                  setOpenId(null);
                  setComposing(m);
                }}
              />
            ) : (
              <>
                <div className="mail-topbar">
                  <div className="mail-search">
                    <Icon name="search" size={16} />
                    <input
                      value={search}
                      placeholder="البحث في البريد"
                      aria-label="بحث"
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                    {search && (
                      <button
                        className="mail-search-clear"
                        onClick={() => setSearch('')}
                        aria-label="مسح البحث"
                      >
                        <Icon name="x" size={14} />
                      </button>
                    )}
                  </div>

                  {/* مهام البريد المطلوب إرساله: تمرّ واحدةً تلو الأخرى، والنقر يفتح القائمة. */}
                  <MailTicker onOpen={() => setTasksOpen(true)} />

                  <button
                    className="btn btn-ghost mail-refresh"
                    onClick={() => sync.mutate()}
                    disabled={sync.isPending}
                    title="مزامنة مع خادم البريد — تلقائيًّا كل دقيقتين"
                  >
                    <Icon name="refresh" size={16} className={sync.isPending ? 'spin' : undefined} />
                    مزامنة
                  </button>

                </div>

                {messages.isPending && <div className="skeleton" style={{ height: 300 }} />}

                {messages.data?.items.length === 0 && (
                  <EmptyState
                    title="لا رسائل"
                    text={
                      search || folder !== 'all'
                        ? 'لا نتائج لهذه التصفية.'
                        : 'اضغط مزامنة لجلب الرسائل من خادم البريد.'
                    }
                  />
                )}

                <div className="mail-list" role="list">
                  {messages.data?.items.map((m) => (
                    <MailRow
                      key={m.id}
                      m={m}
                      onOpen={() => setOpenId(m.id)}
                      onFlag={(flags) => setFlags.mutate({ id: m.id, flags })}
                    />
                  ))}
                </div>

                {/* الترقيم أسفل القائمة — المكوّن نفسه الذي في قوائم العملاء. */}
                {messages.data && (
                  <div className="mail-pager">
                    <Pager
                      page={messages.data.page}
                      pageCount={messages.data.pageCount}
                      total={messages.data.total}
                      pageSize={pageSize}
                      onPage={setPage}
                      onPageSize={(n) => {
                        setPageSize(n);
                        setPage(1);
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}

      {composing && (
        <MailCompose
          replyTo={
            composing === 'new'
              ? null
              : { ...composing, mailAccountId: composing.mailAccount.id }
          }
          onClose={() => setComposing(null)}
          onSent={refresh}
        />
      )}

      {tasksOpen && (
        <MailTasksModal
          onClose={() => setTasksOpen(false)}
          onOpen={(link) => {
            setTasksOpen(false);
            navigate(link);
          }}
        />
      )}
    </div>
  );
}

/* ========================================================================== */

/**
 * شريط نشاط البريد الحيّ — بجوار زرّ التحديث.
 *
 * يمرّ آخرُ ما جرى فعلًا في البريد (رسالةٌ أُرسلت أو وصلت) واحدًا تلو الآخر،
 * كلٌّ يظهر ثوانيَ ثم يخبو ويحلّ محلّه التالي. البيانات حقيقية لا زخرفة: من
 * آخر الرسائل في كل المجلّدات، لا طابورٌ مُتخيَّل. ولذلك لا يظهر شيءٌ حين لا
 * بريد بعد.
 *
 * ويُعاد سؤاله كل نصف دقيقة، وتُبطله المزامنة والإرسال فورًا (مفتاحه يبدأ بـ
 * `mail`)، فما يمرّ في الشريط هو أحدث ما وقع.
 */
/** مدّة عرض كل تسكة — خمس ثوانٍ لتُقرأ كاملةً، لا ومضةٌ تفوت. */
const TICKER_MS = 5000;

interface MailTask {
  id: string;
  subject: string;
  dueAt: string | null;
  status: string;
  mailTo: string | null;
  mailSubject: string | null;
  messageChannel: string | null;
  account: { id: string; name: string; email: string | null } | null;
  lead: { id: string; name: string; email: string | null } | null;
  contact: { id: string; name: string; email: string | null } | null;
  deal: { id: string; title: string } | null;
}

/** عنوان الإرسال — المكتوب في النشاط أولًا ثم بريد السجل المرتبط. */
function taskEmail(t: MailTask): string | null {
  return t.mailTo ?? t.lead?.email ?? t.contact?.email ?? t.account?.email ?? null;
}

/** الجهة المرتبطة بالتسكة — من يُرسَل إليه. */
function taskWho(t: MailTask): string {
  return t.account?.name ?? t.lead?.name ?? t.deal?.title ?? '';
}

/**
 * تسكات البريد = أنشطةٌ من نوع «رسالة بريدية» لم تُنجز بعد.
 *
 * لا الصادرُ في صندوق البريد: هذه مهامٌّ على المندوب أن يرسلها — «أرسل
 * التقرير الشهري لفلان» — يقرؤها من مركز إدارة العملاء ويراها هنا بجوار
 * بريده. يتشاركها الشريط والقائمة عبر مفتاح واحد.
 */
function useMailTasks(pageSize: number) {
  return useQuery({
    queryKey: ['crm', 'mail-tasks', pageSize],
    queryFn: async () => {
      const page = await api<{ items: MailTask[] }>(
        `/crm/activities${qs({ type: 'MAIL', status: 'PLANNED', page: 1, pageSize })}`,
      );
      /* البريد وحده هنا — واتساب وأخواتها تظهر في «قنوات ميتا» عند أهلها. */
      return {
        items: page.items.filter((t) => !t.messageChannel || t.messageChannel === 'EMAIL'),
      };
    },
    refetchInterval: 60_000,
  });
}

function MailTicker({ onOpen }: { onOpen: () => void }) {
  const tasks = useMailTasks(8);
  const items = tasks.data?.items ?? [];
  const [i, setI] = useState(0);

  /* التقدّم خطوةً كلَّ تسكة؛ يتوقّف حين لا عناصر. */
  useEffect(() => {
    if (items.length <= 1) return;
    const t = window.setInterval(() => setI((x) => x + 1), TICKER_MS);
    return () => window.clearInterval(t);
  }, [items.length]);

  /* لا رسائل بريدية معلّقة: لا شريط — لا شيء «مفروض يترسل». */
  if (items.length === 0) return null;

  /* واحدةٌ تبقى ظاهرةً؛ الخُفوت والتبدّل لا معنى لهما إلا حين تتعدّد. */
  const cycling = items.length > 1;
  const m = items[i % items.length]!;
  const who = taskWho(m);

  return (
    <span className="mail-ticker" aria-live="polite">
      {/* زرٌّ: النقر يفتح القائمة كاملةً. وحين تتعدّد يُعيد المفتاح تركيبه كلَّ دورة فتُعاد الحركة. */}
      <button
        type="button"
        key={`${m.id}-${i}`}
        className={`mail-ticker-chip is-out${cycling ? ' is-cycling' : ''}`}
        style={cycling ? { animationDuration: `${TICKER_MS}ms` } : undefined}
        onClick={onOpen}
        title="اعرض مهام البريد المطلوب إرساله"
      >
        <Icon name="mail" size={13} />
        <span className="mail-ticker-text">
          <span className="mail-ticker-verb">للإرسال</span>
          {' — '}
          {m.subject || '(بلا موضوع)'}
          {who && <span className="mail-ticker-who"> · {who}</span>}
        </span>
      </button>
    </span>
  );
}

/**
 * قائمة رسائل البريد المطلوب إرسالها — نافذةٌ تفتح بالنقر على الشريط.
 *
 * ما يمرّ في الشريط واحدًا تلو الآخر يُعرض هنا مجتمعًا: كل مهمّة بموضوعها
 * وصاحبها وموعدها، وبنقرةٍ يُفتح ملفّ صاحبها لينفَّذ الإرسال في سياقه.
 */
function MailTasksModal({
  onClose,
  onOpen,
}: {
  onClose: () => void;
  onOpen: (link: string) => void;
}) {
  const queryClient = useQueryClient();
  const tasks = useMailTasks(25);
  const items = tasks.data?.items ?? [];
  const now = Date.now();

  /* الإرسال من هنا مباشرةً — والنشاط يُعلَّم منجزًا حين تخرج الرسالة. */
  const [sending, setSending] = useState<MailTask | null>(null);

  const completeTask = useMutation({
    mutationFn: (taskId: string) =>
      api(`/crm/activities/${taskId}/complete`, {
        method: 'POST',
        body: { outcome: 'أُرسلت من صندوق البريد.' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crm', 'mail-tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });

  if (sending) {
    return (
      <MailCompose
        to={taskEmail(sending) ?? ''}
        subject={sending.mailSubject ?? sending.subject}
        link={{
          leadId: sending.lead?.id,
          accountRefId: sending.account?.id,
          contactId: sending.contact?.id,
        }}
        onClose={() => setSending(null)}
        onSent={() => {
          completeTask.mutate(sending.id);
          setSending(null);
        }}
      />
    );
  }

  return (
    <Modal title="مهام البريد المطلوب إرساله" onClose={onClose}>
      {tasks.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {tasks.isError && <ErrorState error={tasks.error} onRetry={() => void tasks.refetch()} />}

      {tasks.data && items.length === 0 && (
        <EmptyState
          title="لا رسائل معلّقة"
          text="مهام البريد (نوع «رسالة بريدية») غير المنجزة تظهر هنا لتُرسلها."
        />
      )}

      {items.length > 0 && (
        <div className="mail-tasks">
          {items.map((t) => {
            const who = taskWho(t);
            const link = t.account
              ? `/crm/accounts/${t.account.id}`
              : t.lead
                ? `/crm/leads/${t.lead.id}`
                : '/crm/activities';
            const overdue = t.dueAt && new Date(t.dueAt).getTime() < now;
            const email = taskEmail(t);
            return (
              <button key={t.id} type="button" className="mail-task" onClick={() => onOpen(link)}>
                <span className="mail-task-icon">
                  <Icon name="mail" size={15} />
                </span>
                <span className="mail-task-body">
                  <span className="mail-task-subject">{t.subject || '(بلا موضوع)'}</span>
                  <span className="mail-task-meta">
                    {who || '—'}
                    {t.dueAt && (
                      <span style={{ color: overdue ? 'var(--danger)' : undefined }}>
                        {' · '}
                        {overdue ? 'فات موعدها ' : ''}
                        {mailWhen(t.dueAt)}
                      </span>
                    )}
                  </span>
                </span>
                {/*
                  «أرسل الآن» يفتح الكتابة بعنوان السجل جاهزًا، والإرسال
                  يُعلّم النشاط منجزًا — لا خطوة يدوية تُنسى بعده.
                */}
                {email ? (
                  <span
                    className="btn btn-sm"
                    role="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSending(t);
                    }}
                  >
                    <Icon name="send" size={13} />
                    أرسل الآن
                  </span>
                ) : (
                  <span className="tiny mute">بلا بريد</span>
                )}
                <Icon name="chevronLeft" size={16} className="mail-task-go" />
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

/**
 * صف رسالة — كصفوف Gmail.
 *
 * النجمة في أوله تُقلب دون فتح، وغير المقروءة بيضاء وخطّها ثقيل بينما
 * المقروءة أغمق خلفيةً — فالمقياس يُدرَك قبل القراءة. وعند المرور تظهر
 * الإجراءات مكان التاريخ.
 */
function MailRow({
  m,
  onOpen,
  onFlag,
}: {
  m: MessageRow;
  onOpen: () => void;
  onFlag: (flags: { isRead?: boolean; isStarred?: boolean }) => void;
}) {
  return (
    <div
      className={`gmail-row${m.isRead ? '' : ' is-unread'}`}
      role="listitem"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen();
      }}
      tabIndex={0}
    >
      <button
        className={`gmail-star${m.isStarred ? ' on' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onFlag({ isStarred: !m.isStarred });
        }}
        title={m.isStarred ? 'إلغاء التمييز' : 'تمييز بنجمة'}
        aria-label={m.isStarred ? 'إلغاء التمييز' : 'تمييز بنجمة'}
        aria-pressed={m.isStarred}
      >
        <Icon name="star" size={16} />
      </button>

      <span className="gmail-sender">
        {m.direction === 'OUTBOUND'
          ? `إلى: ${m.toAddresses[0] ?? ''}`
          : m.fromName || m.fromAddress}
      </span>

      <span className="gmail-line">
        <span className="gmail-subject">{m.subject || '(بلا موضوع)'}</span>
        {m.snippet && <span className="gmail-snippet"> — {m.snippet}</span>}
      </span>

      {(m.accountRefId || m.leadId) && (
        <span className="gmail-chip">{m.leadId ? 'عميل محتمل' : 'عميل'}</span>
      )}

      {m.hasAttachments && (
        <span className="gmail-attach" title="بها مرفقات">
          <Icon name="clipboard" size={14} />
        </span>
      )}

      <span className="gmail-when num">{mailWhen(m.sentAt)}</span>

      {/* تظهر عند المرور مكان التاريخ — كإجراءات Gmail العائمة. */}
      <span className="gmail-actions">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onFlag({ isRead: !m.isRead });
          }}
          title={m.isRead ? 'اجعلها غير مقروءة' : 'اجعلها مقروءة'}
          aria-label={m.isRead ? 'اجعلها غير مقروءة' : 'اجعلها مقروءة'}
        >
          <Icon name={m.isRead ? 'mail' : 'mailOpen'} size={15} />
        </button>
      </span>
    </div>
  );
}

/**
 * توقيت الصف كتوقيت Gmail: رسالة اليوم بساعتها، والأقدم بيومها وشهرها.
 *
 * «منذ ٣ أيام» تكفي في سجل نشاط، أما قائمة بريد تُمسح بالعين فتاريخ
 * محدَّد يُقارَن أسرع من حسابٍ ذهني.
 */
function mailWhen(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });
  }
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString('ar-EG', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/* ========================================================================== */

/**
 * الرسالة مفتوحة في مكان القائمة — لا نافذة فوقها.
 *
 * كما يفعل Gmail: سهم يعيد إلى القائمة، والصفحة خلف الرسالة هي سياقها
 * لا خلفية معتمة.
 */
function MessageView({
  id,
  onBack,
  onChanged,
  onReply,
}: {
  id: string;
  onBack: () => void;
  onChanged: () => void;
  onReply: (m: MessageDetail) => void;
}) {
  const message = useQuery({
    queryKey: ['mail', 'message', id],
    queryFn: async () => {
      const m = await api<MessageDetail>(`/mail/messages/${id}`);
      // الفتح يعني القراءة: تعليمها يدويًا خطوة زائدة لا يفعلها أحد.
      if (!m.isRead) {
        await api(`/mail/messages/${id}/flags`, { method: 'PATCH', body: { isRead: true } });
        onChanged();
      }
      return m;
    },
  });

  const flag = useMutation({
    mutationFn: (isStarred: boolean) =>
      api(`/mail/messages/${id}/flags`, { method: 'PATCH', body: { isStarred } }),
    onSuccess: onChanged,
  });

  const m = message.data;

  return (
    <div className="mail-open">
      <div className="mail-open-bar">
        <button className="btn btn-icon btn-ghost" onClick={onBack} aria-label="رجوع إلى القائمة">
          <Icon name="arrowLeft" size={17} />
        </button>

        {m && (
          <button
            className={`gmail-star standalone${m.isStarred ? ' on' : ''}`}
            onClick={() => flag.mutate(!m.isStarred)}
            disabled={flag.isPending}
            title={m.isStarred ? 'إلغاء التمييز' : 'تمييز بنجمة'}
            aria-label={m.isStarred ? 'إلغاء التمييز' : 'تمييز بنجمة'}
          >
            <Icon name="star" size={17} />
          </button>
        )}
      </div>

      {message.isPending && <div className="skeleton" style={{ height: 240 }} />}

      {m && (
        <div className="mail-open-body">
          <h2 className="mail-open-subject">{m.subject || '(بلا موضوع)'}</h2>

          <div className="mail-open-head">
            <span className="avatar avatar-tinted" style={{ width: 40, height: 40 }}>
              {initials(m.fromName || m.fromAddress)}
            </span>

            <div className="mail-open-who">
              <div>
                <strong>{m.fromName || m.fromAddress}</strong>{' '}
                <span className="tiny mute num" dir="ltr">
                  &lt;{m.fromAddress}&gt;
                </span>
              </div>
              <div className="tiny mute">
                إلى: <span className="num" dir="ltr">{m.toAddresses.join('، ')}</span>
                {m.ccAddresses.length > 0 && (
                  <>
                    {' '}
                    — نسخة: <span className="num" dir="ltr">{m.ccAddresses.join('، ')}</span>
                  </>
                )}
              </div>
            </div>

            <span className="tiny mute" style={{ marginInlineStart: 'auto' }}>
              {dateTime(m.sentAt)}
            </span>
          </div>

          {(m.accountRefId || m.leadId) && (
            <div className="row mb-3" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
              {m.accountRefId && (
                <Link to={`/crm/accounts/${m.accountRefId}`} className="gmail-chip">
                  ملف العميل
                </Link>
              )}
              {m.leadId && (
                <Link to={`/crm/leads/${m.leadId}`} className="gmail-chip">
                  العميل المحتمل
                </Link>
              )}
            </div>
          )}

          {/*
            المتن يُعرض نصًا مجرَّدًا لا HTML.
            عرض HTML قادم من الخارج داخل الصفحة يفتح الباب لسكربت في رسالة
            يقرأ جلسة المستخدم. النص المجرَّد يكفي للقراءة والرد.
          */}
          <div className="doc-text mail-body">
            {m.bodyText || '(الرسالة بصيغة HTML فقط — افتحها من برنامج بريدك لعرضها منسّقة)'}
          </div>

          {m.attachments.length > 0 && (
            <div className="mt-4">
              <h4 className="section-title">المرفقات ({m.attachments.length})</h4>
              <div className="col" style={{ gap: 4 }}>
                {m.attachments.map((a, i) => (
                  <div key={i} className="row" style={{ gap: 8 }}>
                    <Icon name="clipboard" size={14} />
                    <span className="small">{a.filename}</span>
                    <span className="tiny mute num">{Math.round(a.size / 1024)} ك.ب</span>
                  </div>
                ))}
              </div>
              <span className="tiny mute">
                الملفات تبقى على خادم البريد؛ افتحها من برنامج بريدك لتنزيلها.
              </span>
            </div>
          )}

          <div className="mt-5">
            <button className="btn mail-reply-btn" onClick={() => onReply(m)}>
              <Icon name="send" size={15} />
              رد
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
